import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Search, Plus, Loader2, Globe, Sparkles, ArrowRight, Info, X, MessageSquare, RefreshCw } from "lucide-react";
import { VendorsTable } from "@/components/vendors";
import StartEvaluationModal from "@/components/modals/StartEvaluationModal";
import FeedbackModal from "@/components/modals/FeedbackModal";
import ConfirmRemoveVendorModal from "@/components/modals/ConfirmRemoveVendorModal";
import { api } from "@/services/api";
import type { WorkspaceVendor, LLMModelId } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { GLEAN_DEMO_VENDOR } from "@/data/glean-mock";

const POLL_INTERVAL_MS = 3000;

/**
 * Upper bound on a single run of continuous polling. Without it, a vendor stuck
 * in `processing` keeps an abandoned tab hitting the API forever, which holds a
 * Cloud Run instance up indefinitely.
 */
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

/**
 * Main dashboard page showing workspace vendors with evaluation status.
 */
export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vendors, setVendors] = useState<WorkspaceVendor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddVendorModalOpen, setIsAddVendorModalOpen] = useState(false);
  const [evaluatingVendorId, setEvaluatingVendorId] = useState<string | null>(null);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [vendorToRemove, setVendorToRemove] = useState<{ id: string; name: string } | null>(null);
  
  const isAdmin = user?.role === 'admin';

  // Admin toast notification
  const [adminToast, setAdminToast] = useState<string | null>(null);
  const adminToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showAdminToast = (message: string) => {
    if (adminToastTimerRef.current) clearTimeout(adminToastTimerRef.current);
    setAdminToast(message);
    adminToastTimerRef.current = setTimeout(() => setAdminToast(null), 10000);
  };

  // Auto-evaluate tracking for non-admin users
  const [autoEvaluateVendorIds, setAutoEvaluateVendorIds] = useState<Set<string>>(new Set());
  const autoEvaluateVendorIdsRef = useRef<Set<string>>(new Set());
  autoEvaluateVendorIdsRef.current = autoEvaluateVendorIds;

  // Use refs to track state for polling without re-creating interval
  const vendorsRef = useRef<WorkspaceVendor[]>([]);
  vendorsRef.current = vendors;

  const evaluatingVendorIdRef = useRef<string | null>(null);
  evaluatingVendorIdRef.current = evaluatingVendorId;

  // Polling stops after MAX_POLL_DURATION_MS and only resumes on a page reload
  const [isPollingStopped, setIsPollingStopped] = useState(false);
  const pollStartedAtRef = useRef<number | null>(null);

  // Initial load
  useEffect(() => {
    loadVendors();
  }, []);

  // Polling for in-progress evaluations
  useEffect(() => {
    const pollInterval = setInterval(() => {
      // Check if we have any in-progress evaluations, processing vendors, or if we just started one
      const hasInProgress = vendorsRef.current.some(
        v => v.evaluation_status === "pending" || v.evaluation_status === "processing"
      );
      const hasProcessingVendors = vendorsRef.current.some(
        v => v.vendor_status === "processing"
      );
      const isWaitingForEvaluation = evaluatingVendorIdRef.current !== null;

      const hasAutoEvaluateQueue = autoEvaluateVendorIdsRef.current.size > 0;

      if (!hasInProgress && !hasProcessingVendors && !isWaitingForEvaluation && !hasAutoEvaluateQueue) {
        // Idle, so the next burst of activity starts with a full time budget
        pollStartedAtRef.current = null;
        return;
      }

      if (pollStartedAtRef.current === null) {
        pollStartedAtRef.current = Date.now();
      }

      if (Date.now() - pollStartedAtRef.current >= MAX_POLL_DURATION_MS) {
        clearInterval(pollInterval);
        setIsPollingStopped(true);
        return;
      }

      loadVendors();
    }, POLL_INTERVAL_MS); // Poll every 3 seconds when there's activity

    return () => clearInterval(pollInterval);
  }, []); // Empty dependency - only run once

  // Auto-evaluate vendors when they finish crawling (non-admin only)
  useEffect(() => {
    if (autoEvaluateVendorIds.size === 0) return;

    const idsToRemove: string[] = [];
    const idsToEvaluate: string[] = [];

    for (const vendorId of autoEvaluateVendorIds) {
      const vendor = vendors.find(v => v.vendor_id === vendorId);
      if (!vendor) continue;

      // Skip if vendor crawl failed
      if (vendor.vendor_status === 'error') {
        idsToRemove.push(vendorId);
        continue;
      }

      // Skip if still processing
      if (vendor.vendor_status === 'processing') continue;

      // Skip if already has evaluation or is being evaluated
      if (vendor.has_evaluation || vendor.evaluation_status || evaluatingVendorId === vendorId) {
        idsToRemove.push(vendorId);
        continue;
      }

      // Vendor is ready — queue for evaluation
      idsToEvaluate.push(vendorId);
      idsToRemove.push(vendorId);
    }

    if (idsToRemove.length > 0 || idsToEvaluate.length > 0) {
      // Remove processed IDs from the queue
      setAutoEvaluateVendorIds(prev => {
        const next = new Set(prev);
        for (const id of [...idsToRemove, ...idsToEvaluate]) {
          next.delete(id);
        }
        return next;
      });

      // Fire evaluation for ready vendors
      for (const vendorId of idsToEvaluate) {
        handleEvaluate(vendorId);
      }
    }
  }, [vendors, autoEvaluateVendorIds]);

  const loadVendors = async () => {
    try {
      // Get the active workspace ID
      const activeWorkspaceId = localStorage.getItem("active_workspace_id");
      if (!activeWorkspaceId) {
        console.warn("No active workspace ID found");
        setIsLoading(false);
        return;
      }
      
      setWorkspaceId(activeWorkspaceId);
      
      const { vendors: workspaceVendors } = await api.getWorkspaceVendors(activeWorkspaceId);
      setVendors(workspaceVendors);
      
      // Clear evaluatingVendorId if the vendor now has an evaluation status
      const currentEvaluatingId = evaluatingVendorIdRef.current;
      if (currentEvaluatingId) {
        const evaluatingVendor = workspaceVendors.find(v => v.vendor_id === currentEvaluatingId);
        if (evaluatingVendor?.has_evaluation || evaluatingVendor?.evaluation_status) {
          setEvaluatingVendorId(null);
        }
      }
    } catch (err) {
      console.error("Failed to load vendors:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEvaluate = async (vendorId: string, model?: LLMModelId) => {
    setEvaluatingVendorId(vendorId);
    try {
      await api.startEvaluation(vendorId, model ? { model } : undefined);
      // Reload vendors to get updated evaluation status
      await loadVendors();
      // Keep evaluatingVendorId set - it will be cleared by the next poll
      // when we see the vendor has an evaluation status
    } catch (err) {
      console.error("Failed to start evaluation:", err);
      // Only clear on error
      setEvaluatingVendorId(null);
    }
  };

  const handleRemoveVendor = (vendorId: string, vendorName: string) => {
    setVendorToRemove({ id: vendorId, name: vendorName });
  };

  const confirmRemoveVendor = async () => {
    if (!workspaceId || !vendorToRemove) return;

    try {
      await api.removeVendorFromWorkspace(workspaceId, vendorToRemove.id);
      await loadVendors();
    } catch (err) {
      console.error("Failed to remove vendor:", err);
    } finally {
      setVendorToRemove(null);
    }
  };

  const handleReAnalyze = async (vendorId: string, maxPages: number) => {
    const vendor = vendors.find(v => v.vendor_id === vendorId);
    if (!vendor?.vendor_domain) return;

    try {
      await api.analyzeAsync(vendor.vendor_domain, {
        max_pages: maxPages,
        force_refresh: true,
      });
      await loadVendors();
    } catch (err) {
      console.error("Failed to re-analyze vendor:", err);
    }
  };

  const handleVendorAdded = async (vendorId: string) => {
    // After a vendor is added/analyzed, add it to the workspace and reload
    if (workspaceId && vendorId && vendorId !== 'pending') {
      try {
        await api.addVendorToWorkspace(workspaceId, vendorId, 'public');
      } catch (err) {
        // Vendor might already be in workspace, that's ok
        console.log('Vendor may already be in workspace:', err);
      }

      // For non-admin users, queue vendor for auto-evaluation once crawl completes
      if (!isAdmin) {
        setAutoEvaluateVendorIds(prev => new Set([...prev, vendorId]));
      } else {
        showAdminToast(
          "Because you're an admin, the evaluation was not triggered automatically. Please click Evaluate and choose the LLM you want to use."
        );
      }
    }
    // Reload vendors to show the newly added vendor
    loadVendors();
  };

  // For admin users, prepend the hardcoded Glean demo vendor
  const allVendors = isAdmin
    ? [GLEAN_DEMO_VENDOR, ...vendors.filter(v => v.vendor_id !== GLEAN_DEMO_VENDOR.vendor_id)]
    : vendors;

  const filteredVendors = allVendors.filter(vendor =>
    vendor.vendor_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (vendor.vendor_domain?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false)
  );

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-8 bg-background">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
          {/* Left: Title and search */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-lg font-semibold text-foreground">My Vendors</h2>
              {allVendors.length > 0 && (
                <span className="px-3 py-1 text-sm font-semibold bg-primary text-primary-foreground rounded-full shadow-sm">
                  {allVendors.length}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              {isAdmin
                ? 'Vendors in your workspace. Click "Evaluate" to run an evaluation.'
                : 'Vendors in your workspace. Evaluations run automatically when added.'}
            </p>

            {/* Search bar */}
            {allVendors.length > 0 && (
              <div className="relative max-w-lg">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search vendors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-9 w-full pl-10 pr-4 border border-input bg-background text-sm font-medium text-foreground placeholder:text-muted-foreground rounded-md shadow-sm transition-colors focus:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-ring hover:bg-accent hover:text-accent-foreground"
                />
              </div>
            )}
          </div>

          {/* Right: Action buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => setIsFeedbackModalOpen(true)}
            >
              <MessageSquare className="h-4 w-4" />
              Feedback
            </Button>
            <Button
              variant="brand"
              className="flex items-center gap-2"
              onClick={() => setIsAddVendorModalOpen(true)}
            >
              <Plus className="h-4 w-4" />
              Add Vendor
            </Button>
          </div>
        </div>

        {/* Show loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
            <span className="text-muted-foreground">Loading vendors...</span>
          </div>
        )}

        {/* Empty state when no vendors */}
        {!isLoading && allVendors.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 px-4 border border-dashed border-border rounded-lg">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
              <Globe className="w-10 h-10 text-primary" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Get started by adding a vendor
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-md mb-6">
              Add an AI vendor to your workspace by entering their website URL. We'll crawl and analyze it so you can run evaluations.
            </p>
            <Button 
              variant="brand" 
              size="lg"
              className="flex items-center gap-2"
              onClick={() => setIsAddVendorModalOpen(true)}
            >
              <Plus className="h-5 w-5" />
              Add Your First Vendor
            </Button>
          </div>
        )}

        {/* Vendors Table - only show when there are vendors */}
        {!isLoading && allVendors.length > 0 && (
          <VendorsTable
            vendors={filteredVendors}
            isLoading={false}
            onEvaluate={handleEvaluate}
            onReAnalyze={isAdmin ? handleReAnalyze : undefined}
            onRemove={isAdmin ? handleRemoveVendor : undefined}
            evaluatingVendorId={evaluatingVendorId}
            isAdmin={isAdmin}
          />
        )}

        {/* What's Next CTA - Buy vs Build (Admin only) */}
        {isAdmin && !isLoading && allVendors.length > 0 && (
          <div className="mt-8">
            <button
              onClick={() => navigate('/strategy')}
              className="w-full rounded-2xl border border-[#D4893E]/30 bg-card p-6 flex items-center justify-between group transition-all duration-200 hover:border-[#D4893E]/50 hover:shadow-md active:scale-[0.99]"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4893E] to-[#063F6B] flex items-center justify-center shadow-sm">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-foreground">
                    What Should We Do Next?
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Explore Buy vs Build decision framework
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-[#D4893E] font-medium">
                <span className="hidden sm:inline text-sm">Explore</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          </div>
        )}

        {/* Add Vendor Modal */}
        <StartEvaluationModal
          isOpen={isAddVendorModalOpen}
          onClose={() => {
            setIsAddVendorModalOpen(false);
            // Reload vendors when modal closes to pick up any newly added vendors
            loadVendors();
          }}
          onStartEvaluation={handleVendorAdded}
          existingVendorIds={new Set(allVendors.map(v => v.vendor_id))}
          workspaceId={workspaceId}
        />

        {/* Feedback Modal */}
        <FeedbackModal
          isOpen={isFeedbackModalOpen}
          onClose={() => setIsFeedbackModalOpen(false)}
        />

        {/* Confirm Remove Vendor Modal (Admin only) */}
        <ConfirmRemoveVendorModal
          isOpen={!!vendorToRemove}
          onClose={() => setVendorToRemove(null)}
          onConfirm={confirmRemoveVendor}
          vendorName={vendorToRemove?.name ?? ""}
        />

      </div>

      {/* Toast stack */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        {/* Polling stopped notification */}
        {isPollingStopped && (
          <div className="max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-300">
            <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 shadow-lg">
              <RefreshCw className="h-4 w-4 text-primary shrink-0" />
              <p className="text-sm text-foreground">
                Live updates paused.{" "}
                <button
                  onClick={() => window.location.reload()}
                  className="font-medium text-primary underline underline-offset-2 hover:no-underline"
                >
                  Click here to refresh
                </button>
              </p>
            </div>
          </div>
        )}

        {/* Admin toast notification */}
        {adminToast && (
          <div className="max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-300">
            <div className="flex items-start gap-3 rounded-lg border border-border bg-card p-4 shadow-lg">
              <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-foreground">{adminToast}</p>
              <button
                onClick={() => {
                  setAdminToast(null);
                  if (adminToastTimerRef.current) clearTimeout(adminToastTimerRef.current);
                }}
                className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
